import { clientToolOf } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { Button, Card, Col, Empty, Row, Spin, Tag, Typography } from 'antd';
import { enUS, ru } from 'date-fns/locale';
import React from 'react';
import { GROUP_LABEL_KEYS, toolLabel } from '../../components/clientTools/labels';
import type { ClientSection } from './clientPageModel';
import { formatDay } from '../caseload/formatDay';

/**
 * E57 · ClientDigest — the Overview's default pill "Since the last session" (cc-practice, screen "Client page — Overview —
 * since the last session", e57e0001-…-071; Q-e57g1-6 A). One card per tool with new activity since the end of the latest
 * session — homework done of assigned (E34's own count), the latest score and its change, check-ins and their mood, diary
 * days, sleep nights, new thought tests, any other tool's new records — each with "Open …" to its section. A tool the
 * therapist hid STILL shows its card, marked "hidden by you" (hiding trims navigation, never what is true about the client).
 *
 * Behavior lines implemented here:
 *   · nav: each card opens its tool's section (clientPage.digest.open);
 *   · format: "since" is the end of the latest session (clientPage.digest.since);
 *   · empty-state: no session yet → the last 14 days; nothing new → "Nothing new since the last session"
 *     (clientPage.digest.noSession / clientPage.digest.empty);
 *   · help: the no-monitoring line (clientPage.digest.noMonitoring).
 */
type DigestCard = {
  tool: string;
  group: string;
  count: number;
  hidden: boolean;
  lastAt?: string | null;
  lastDay?: string | null;
  done?: number | null;
  total?: number | null;
  overdue?: number | null;
  moodMin?: number | null;
  moodMax?: number | null;
  scores: Array<{ instrument: string; score: number; previous?: number | null; change?: number | null; at: string }>;
};

export const DIGEST_FIELDS = [
  'subject',
  'sinceDate',
  'noSession',
  'workShared',
  {
    cards: [
      'tool',
      'group',
      'count',
      'hidden',
      'lastAt',
      'lastDay',
      'done',
      'total',
      'overdue',
      'moodMin',
      'moodMax',
      { scores: ['instrument', 'score', 'previous', 'change', 'at'] },
    ],
  },
];

export const ClientDigestSection: React.FC<{
  clientId: string;
  locale: 'en' | 'ru';
  onNavigate: (section: ClientSection) => void;
}> = ({ clientId, locale, onNavigate }) => {
  const t = useTranslate();
  const dateLocale = locale === 'ru' ? ru : enUS;
  const day = (iso: string) => formatDay(iso, locale === 'ru' ? 'EEEEEE d MMM' : 'EEE d MMM', dateLocale);

  const { data, isLoading, isError } = useCustom<{
    sinceDate: string;
    noSession: boolean;
    workShared: boolean;
    cards: DigestCard[];
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientDigest',
      fields: DIGEST_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });
  const digest = data?.data;

  const headline = (c: DigestCard): React.ReactNode => {
    switch (c.tool) {
      case 'homework':
        return (
          <>
            {t('clientPage.digest.homework', { done: c.done ?? 0, total: c.total ?? 0 })}
            {c.overdue ? (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                {t('clientPage.digest.overdue', { count: c.overdue })}
              </Tag>
            ) : null}
          </>
        );
      case 'assessments':
        return c.scores.map((s) => (
          <div key={s.instrument} data-testid={`cc-client-digest-score-${s.instrument}`}>
            {t('clientPage.digest.score', { instrument: s.instrument, score: s.score })}
            {s.previous !== null && s.previous !== undefined ? (
              <Typography.Text type="secondary" style={{ marginLeft: 6 }}>
                {t('clientPage.digest.scoreWas', { previous: s.previous })}
              </Typography.Text>
            ) : null}
          </div>
        ));
      case 'checkins':
        return (
          <>
            {t('clientPage.digest.checkins', { count: c.count })}
            {c.moodMin !== null && c.moodMin !== undefined ? (
              <div>
                <Typography.Text type="secondary">
                  {t('clientPage.digest.mood', { min: c.moodMin, max: c.moodMax })}
                </Typography.Text>
              </div>
            ) : null}
          </>
        );
      case 'diarycard':
        return t('clientPage.digest.diaryDays', { count: c.count });
      case 'sleep-diary':
        return t('clientPage.digest.sleepNights', { count: c.count });
      default:
        return t('clientPage.digest.records', { count: c.count });
    }
  };

  return (
    <section data-testid="cc-client-digest">
      <Typography.Title level={5} style={{ marginTop: 0 }} data-testid="cc-client-digest-since">
        {digest
          ? digest.noSession
            ? t('clientPage.digest.noSession')
            : t('clientPage.digest.since', { date: day(digest.sinceDate) })
          : null}
      </Typography.Title>
      {isLoading ? <Spin /> : null}
      {isError ? (
        <Typography.Paragraph type="danger" data-testid="cc-client-digest-error">
          {t('clientPage.digest.loadFailed')}
        </Typography.Paragraph>
      ) : null}
      {digest && !digest.cards.length ? (
        <Empty description={t('clientPage.digest.empty')} data-testid="cc-client-digest-empty" />
      ) : null}
      <Row gutter={[12, 12]}>
        {(digest?.cards ?? []).map((c) => {
          const def = clientToolOf(c.tool);
          return (
            <Col key={c.tool} xs={24} sm={12} lg={8}>
              <Card
                size="small"
                data-testid={`cc-client-digest-card-${c.tool}`}
                title={
                  <span>
                    {toolLabel(t, c.tool)}
                    {c.hidden ? (
                      <Tag style={{ marginLeft: 6 }} data-testid={`cc-client-digest-card-${c.tool}-hidden`}>
                        {t('clientPage.tools.hiddenByYou')}
                      </Tag>
                    ) : null}
                  </span>
                }
                extra={def ? <Typography.Text type="secondary">{t(GROUP_LABEL_KEYS[def.group])}</Typography.Text> : null}
              >
                <div data-testid={`cc-client-digest-card-${c.tool}-headline`}>{headline(c)}</div>
                {c.lastDay || c.lastAt ? (
                  <Typography.Text type="secondary">
                    {t('clientPage.digest.last', { date: day(c.lastDay ? `${c.lastDay}T12:00:00` : (c.lastAt as string)) })}
                  </Typography.Text>
                ) : null}
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => def && onNavigate({ kind: 'tool', tool: def.key, group: def.group })}
                    data-testid={`cc-client-digest-open-${c.tool}`}
                  >
                    {t('clientPage.digest.open', { tool: toolLabel(t, c.tool) })}
                  </Button>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }} data-testid="cc-client-digest-no-monitoring">
        {t('clientPage.digest.noMonitoring')}
      </Typography.Paragraph>
    </section>
  );
};
